import React from "react";
import NumberFormat from "react-number-format";

import { MenuItem, Stack, TextField } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { FormikErrors, FormikTouched } from "formik";
import { useDateFormatSelect } from "store";
import { CurrencyType, IInvestmentDetailField, IInvestmentDetailValue } from "store/types";
import toIsoMidday from "utils/to-iso-midday";

import { currenciesArray } from "../../../../constants";
import { CompanyAutocomplete } from "./CompanyAutocomplete";

interface FormikData {
  values: IInvestmentDetailValue[];
  errors: FormikErrors<IInvestmentDetailValue[]>;
  touched: FormikTouched<IInvestmentDetailValue[]>;
}

interface CompanyFieldProps {
  column: IInvestmentDetailField;
  field: IInvestmentDetailValue;
  onFieldChange: (columnId: number, updates: Partial<IInvestmentDetailValue>) => void;
  formik: FormikData;
}

export const CompanyField: React.FC<CompanyFieldProps> = ({ column, field, onFieldChange, formik }) => {
  const dateFormat = useDateFormatSelect();
  const fieldIndex = formik.values.findIndex((f) => f.id === column.id);
  const error = formik.errors?.[fieldIndex];
  const touchedField = formik.touched?.[fieldIndex];

  if (column.with_history) {
    return (
      <Stack direction="row" gap="5px" sx={{ width: "100%" }}>
        <NumberFormat
          thousandSeparator
          isNumericString
          variant="outlined"
          size="small"
          sx={{ width: "35%" }}
          decimalScale={2}
          label={column.name}
          customInput={TextField}
          value={field.value}
          onValueChange={({ value }) => onFieldChange(column.id, { value })}
          suffix={column.data_type === "percentage" ? "%" : ""}
        />
        <DesktopDatePicker
          label="Date"
          format={dateFormat}
          views={["year", "month", "day"]}
          sx={{ width: "35%" }}
          value={field.date ? dayjs(field.date) : null}
          onChange={(newValue) => {
            if (newValue && newValue.isValid()) {
              onFieldChange(column.id, { date: toIsoMidday(newValue.toISOString()) });
            }
          }}
          slotProps={{
            textField: {
              size: "small",
              error: touchedField?.date && Boolean(error?.date),
              helperText: touchedField?.date && error?.date
            }
          }}
        />
        <TextField
          select
          label="Currency"
          size="small"
          sx={{ width: "30%", "& .MuiSelect-select": { padding: "8.5px !important" } }}
          value={field.currency}
          onChange={(e) => onFieldChange(column.id, { currency: e.target.value as CurrencyType })}
        >
          {currenciesArray.map((currency, index) => (
            <MenuItem value={currency} key={index}>
              {currency}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
    );
  }

  if (column.column_type === "number") {
    return (
      <Stack direction="row" gap="5px" sx={{ width: "100%" }}>
        <NumberFormat
          thousandSeparator
          isNumericString
          variant="outlined"
          size="small"
          decimalScale={2}
          sx={{ width: "65%" }}
          label={column.name}
          customInput={TextField}
          value={field.value}
          onValueChange={({ value }) => onFieldChange(column.id, { value })}
          suffix={column.data_type === "percentage" ? "%" : ""}
        />
        <DesktopDatePicker
          label="Date"
          format={dateFormat}
          views={["year", "month", "day"]}
          sx={{ width: "35%" }}
          value={field.date ? dayjs(field.date) : null}
          onChange={(newValue) => {
            if (newValue && newValue.isValid()) {
              onFieldChange(column.id, { date: toIsoMidday(newValue.toISOString()) });
            }
          }}
          slotProps={{
            textField: {
              size: "small",
              error: touchedField?.date && Boolean(error?.date),
              helperText: touchedField?.date && error?.date
            }
          }}
        />
      </Stack>
    );
  }

  return (
    <CompanyAutocomplete
      defaultValue={field.value}
      onChange={(value) => onFieldChange(column.id, { value })}
      investmentId={column.id}
      charName={column.name}
      error={touchedField?.value && Boolean(error?.value)}
      helperText={touchedField?.value && error?.value}
    />
  );
};
